import React from "react";
import { useLocation } from "react-router-dom";
import { ShieldCheck, Headphones, Facebook, Instagram, Twitter, Youtube, Send } from "lucide-react";
import { FaWallet, FaHeadset, FaPeopleArrows, FaTv, FaGift, FaIndianRupeeSign, FaStar } from "react-icons/fa6";
import { useApp } from "../context/AppContext";

// Solid (Font Awesome 6) icons, gold gradient fill + soft glow. `badge` = small solid mark at the bottom-right.
const FEATURES = [
  { icon: FaWallet, badge: FaIndianRupeeSign, label: "Instant Withdrawals & Deposits" },
  { icon: FaHeadset, label: "24/7 Customer Service" },
  { icon: FaPeopleArrows, label: "Affiliate Accounts" },
  { icon: FaTv, label: "Live Stream of Events" },
  { icon: FaGift, badge: FaStar, label: "₹500 Referral Bonus" },
];

function SolidIcon({ icon: Icon, badge: Badge }) {
  return (
    <span className="relative inline-grid place-items-center h-6 w-6 [filter:drop-shadow(0_1px_3px_rgba(245,166,35,0.35))]">
      <svg width="0" height="0" className="absolute" aria-hidden="true">
        <defs><linearGradient id="footer-gold" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#FFC83D" /><stop offset="1" stopColor="#F59E0B" /></linearGradient></defs>
      </svg>
      <Icon size={22} style={{ fill: "url(#footer-gold)", stroke: "none", strokeWidth: 0 }} />
      {Badge && (
        <span className="absolute -bottom-0.5 -right-1 grid place-items-center h-2.5 w-2.5 rounded-full bg-[#0b0b0b] ring-1 ring-[#F59E0B]">
          <Badge size={6} style={{ fill: "#FFC83D" }} />
        </span>
      )}
    </span>
  );
}

const LINKS = ["Privacy Policy", "Rules and Regulations", "KYC", "FAQ", "Responsible Gaming", "Terms and Conditions", "About Us"];

const SOCIAL = [
  { icon: Facebook, label: "Facebook" },
  { icon: Instagram, label: "Instagram" },
  { icon: Twitter, label: "Twitter" },
  { icon: Youtube, label: "YouTube" },
  { icon: Send, label: "Telegram" },
];

const LEGAL = ["Privacy Policy", "Rules & Regulations", "Responsible Gaming", "KYC Policy"];

export default function Footer() {
  const location = useLocation();
  const { isAuthed } = useApp();

  // Logged-out (login/sign-in) state: show only legal links + copyright.
  if (!isAuthed) {
    return (
      <footer className="mt-16 border-t border-white/5 bg-gradient-to-b from-[#0d1420] to-[#080c13]" data-testid="site-footer-minimal">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-28 lg:pb-8">
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-slate-500">
            {LEGAL.map((l) => (
              <span key={l} className="hover:text-primary transition-colors cursor-pointer">{l}</span>
            ))}
          </div>
          <div className="mt-8 text-center text-xs text-slate-600">
            <span>© 2021 BatBallWorld. All rights reserved.</span>
          </div>
        </div>
      </footer>
    );
  }

  // Account tab must end with the Logout button — no site footer below it.
  if (location.pathname.startsWith("/wallet")) return null;
  const isHome = ["/", "/sports", "/live-casino"].includes(location.pathname);

  if (isHome) {
    return (
      <footer className="-mt-20 lg:mt-0 bg-[#0E1520] w-full px-4 pt-6 pb-[90px]" data-testid="site-footer">
        {/* Top row: logo + badges */}
        <div className="flex items-center justify-between gap-3 h-12 border-b border-white/[0.06] pb-3" data-testid="footer-top-row">
          <div className="flex items-center gap-2.5 min-w-0">
            <img src="/img/logo.webp" alt="BatBallWorld" className="h-9 w-9 shrink-0 drop-shadow-[0_0_8px_rgba(245,180,0,0.45)]" />
            <span className="font-display font-extrabold text-[15px] tracking-tight text-white truncate">Bat<span className="text-[#f5b400]">Ball</span>World</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="inline-flex items-center gap-1.5 h-7 rounded-full border border-[#f5b400]/70 text-[#f5b400] text-[11px] font-bold px-2.5 leading-none">
              <ShieldCheck className="h-3.5 w-3.5" /> 100% Safe
            </span>
            <span className="inline-flex items-center gap-1.5 h-7 rounded-full border border-[#f5b400]/70 text-[#f5b400] text-[11px] font-bold px-2.5 leading-none">
              <Headphones className="h-3.5 w-3.5" /> Support
            </span>
          </div>
        </div>

        {/* Feature row: solid gold icons — 3 on the first row, 2 centered on the second */}
        <div className="mt-6 flex flex-wrap justify-center gap-y-3 gap-x-0" data-testid="footer-features">
          {FEATURES.map(({ icon, badge, label }) => (
            <div key={label} className="basis-1/3 flex flex-col items-center text-center gap-1.5 px-1" data-testid={`footer-feature-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <SolidIcon icon={icon} badge={badge} />
              <span className="text-[11px] font-semibold text-[#C9D3DD] leading-tight break-words">{label}</span>
            </div>
          ))}
        </div>

        {/* Links row */}
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3 text-sm text-white" data-testid="footer-links">
          {LINKS.map((l) => (
            <span key={l} className="hover:text-[#f5b400] transition-colors cursor-pointer">{l}</span>
          ))}
        </div>

        {/* Social icons row */}
        <div className="mt-6 flex items-center justify-center gap-3" data-testid="footer-social">
          {SOCIAL.map(({ icon: Icon, label }) => (
            <span key={label} aria-label={label} className="h-9 w-9 rounded-full border border-[#f5b400]/70 text-[#f5b400] grid place-items-center hover:bg-[#f5b400]/10 transition-colors cursor-pointer">
              <Icon className="h-4 w-4" strokeWidth={1.8} />
            </span>
          ))}
        </div>

        <div className="mt-5 text-center text-xs text-slate-500">© 2021 BatBallWorld. All rights reserved.</div>
      </footer>
    );
  }

  return (
    <footer className="mt-16 bg-gradient-to-b from-[#0d1420] to-[#080c13] border-t border-white/5 pb-24 lg:pb-0" data-testid="site-footer">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-slate-500">
          {LEGAL.map((l) => (
            <span key={l} className="hover:text-primary transition-colors cursor-pointer">{l}</span>
          ))}
        </div>
        <div className="mt-4 text-center text-xs text-slate-600">
          <span>© 2021 BatBallWorld. All rights reserved.</span>
        </div>
      </div>
    </footer>
  );
}
